import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCloudSunRain } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider } from "@mui/material";
import { Result } from "antd";
import React from "react";
import CurrentConditions from "../../components/Weather/CurrentConditions";
import Next5Days from "../../components/Weather/Next5Days";
import UpcomingHourly from "../../components/Weather/UpcomingHourly";
import Helper from "../../Helper";
import { CommandProjectInfo } from "../CommandTypes";

interface DashboardWeatherProps {
    data: any;
    project: CommandProjectInfo;
    fetchWeatherData: () => Promise<any>;
}

const DashboardWeather: React.FC<DashboardWeatherProps> = ({ data, project, fetchWeatherData }) => {
    const [weatherData, setWeatherData] = React.useState<any>(data);

    const reloadDataInterval = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        setWeatherData(data);
    }, [data]);

    React.useEffect(() => {
        fetchWeatherData().then((newData) => {
            setWeatherData(newData);
        });

        reloadDataInterval.current = setInterval(
            () => {
                fetchWeatherData().then((newData) => {
                    setWeatherData(newData);
                });
            },
            5 * 60 * 1000
        ); // Update every 5 minutes

        return () => {
            if (reloadDataInterval.current) {
                clearInterval(reloadDataInterval.current);
            }
        };
    }, [project.unique_token]);

    const currentConditionsMemo = React.useMemo(() => {
        if (!weatherData) {
            return null;
        }

        return <CurrentConditions textColor="text-white" weather={weatherData} project={project} />;
    }, [weatherData, project]);
    const upcomingHourlyMemo = React.useMemo(() => {
        if (!weatherData) {
            return null;
        }

        return (
            <UpcomingHourly textColor="text-white" flexDirection="flex-row" weather={weatherData} project={project} />
        );
    }, [weatherData, project]);
    const next5DaysMemo = React.useMemo(() => {
        if (!weatherData) {
            return null;
        }

        return <Next5Days textColor="text-white" flexDirection="flex-row" weather={weatherData} project={project} />;
    }, [weatherData, project]);

    const executiveView = new URL(window.location.href).pathname.includes("executive");

    return (
        <div className="text-white h-full mb-10" style={{ zoom: executiveView ? 0.5 : 1 }}>
            {!weatherData ? (
                <Result
                    className={Helper.classNames("w-full h-full ")}
                    icon={<FontAwesomeIcon icon={faCloudSunRain as IconProp} size="3x" color="#FFFFFF" />}
                    title={<div className="text-white">Weather currently unavailable</div>}
                />
            ) : (
                <div className="flex flex-col h-full w-full justify-between">
                    <div className="flex-0 flex flex-col items-center justify-between">
                        <Divider className="my-4!">
                            <div className="uppercase font-semibold font-plot-open-sans text-gray-300">
                                Current Conditions
                            </div>
                        </Divider>
                        {currentConditionsMemo}
                    </div>
                    <div className="flex-1 mt-4 mb-12">
                        <Divider className="my-4!">
                            <div className="uppercase font-semibold font-plot-open-sans text-gray-300">
                                Hourly Forecast
                            </div>
                        </Divider>
                        {upcomingHourlyMemo}
                    </div>
                    <div className="flex-1">
                        <Divider className="my-4!">
                            <div className="uppercase font-semibold font-plot-open-sans text-gray-300">
                                Daily Forecast
                            </div>
                        </Divider>
                        {next5DaysMemo}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardWeather;
